import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook để xử lý authentication errors
 * Ẩn error messages khi user chưa đăng nhập hoặc session hết hạn
 */
export const useAuthErrorHandler = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated, loading: authLoading } = useAuth();

  /**
   * Hiển thị error message chỉ khi user đã authenticated
   */
  const showError = useCallback((message: string, options?: any) => {
    if (isAuthenticated && !authLoading) {
      enqueueSnackbar(message, { 
        variant: 'error',
        ...options 
      });
    }
  }, [enqueueSnackbar, isAuthenticated, authLoading]);

  /**
   * Hiển thị success message (luôn hiển thị)
   */
  const showSuccess = useCallback((message: string, options?: any) => {
    enqueueSnackbar(message, { 
      variant: 'success',
      ...options 
    });
  }, [enqueueSnackbar]);

  /**
   * Hiển thị warning message (luôn hiển thị)
   */
  const showWarning = useCallback((message: string, options?: any) => {
    enqueueSnackbar(message, { 
      variant: 'warning',
      ...options 
    });
  }, [enqueueSnackbar]);

  /**
   * Hiển thị info message (luôn hiển thị)
   */
  const showInfo = useCallback((message: string, options?: any) => {
    enqueueSnackbar(message, { 
      variant: 'info',
      ...options 
    });
  }, [enqueueSnackbar]);

  /**
   * Kiểm tra xem có nên hiển thị error hay không
   */
  const shouldShowError = useCallback(() => {
    return isAuthenticated && !authLoading;
  }, [isAuthenticated, authLoading]);

  return {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    shouldShowError
  };
};

/**
 * Wrapper cho useSnackbar để tự động xử lý authentication errors
 */
export const useAuthAwareSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const authAwareEnqueueSnackbar = useCallback((message: string, options?: any) => {
    // Chỉ hiển thị error messages khi user đã authenticated
    if (options?.variant === 'error' && (!isAuthenticated || authLoading)) {
      return;
    }
    
    enqueueSnackbar(message, options);
  }, [enqueueSnackbar, isAuthenticated, authLoading]);

  return {
    enqueueSnackbar: authAwareEnqueueSnackbar,
    closeSnackbar: useSnackbar().closeSnackbar
  };
};
